<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Reglas de Cálculo</h1>
    </div>

    <div class="row">
      <div
        v-for="card in cards"
        :key="card.id"
        class="col-lg-4 col-md-6 mb-4"
      >
        <b-card no-body class="custom-card custom-form h-100">
          <b-card-header class="custom-card-header d-flex align-items-center justify-content-between">
            <h3 class="mb-0">{{ card.name }}</h3>
            <span class="badge badge-secondary">{{ card.code }}</span>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section">

              <!-- Descripción -->
              <div class="form-group">
                <label>Descripción</label>
                <p v-if="!card.editing" class="description-title mb-0 text-muted">{{ card.description || '—' }}</p>
                <input
                  v-else
                  type="text"
                  v-model="card.form.description"
                  placeholder="Ingresar descripción"
                  class="custom-form-control"
                />
              </div>

              <!-- Fórmula -->
              <div class="form-group">
                <label>Fórmula</label>
                <div v-if="!card.editing" class="formula-display">
                  <code>{{ card.formula || '—' }}</code>
                </div>
                <div v-else>
                  <input
                    type="text"
                    v-model="card.form.formula"
                    placeholder="Ej: CEILING(base * 1.5, 10)"
                    class="custom-form-control"
                    :class="{'custom-form-control-error': card.formulaError}"
                    @input="onFormulaInput(card)"
                  />
                  <span v-if="card.formulaError" class="form-error">{{ card.formulaError }}</span>
                </div>
              </div>

              <!-- Simulador -->
              <div class="form-group mb-0">
                <label>Simulador</label>
                <div class="simulator-row">
                  <div class="d-flex align-items-center">
                    <span class="simulator-label">Costo base</span>
                    <input
                      type="number"
                      v-model="card.baseInput"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      class="custom-form-control simulator-input"
                      @input="onBaseInput(card)"
                    />
                  </div>
                  <div class="simulator-result mt-2">
                    <span class="simulator-label">Resultado</span>
                    <span v-if="card.simulateResult !== null" class="simulator-value">
                      S/ {{ parseFloat(card.simulateResult).toFixed(2) }}
                    </span>
                    <span v-else-if="card.simulateError" class="simulator-error">
                      {{ card.simulateError }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </div>
                </div>
              </div>

            </section>

            <!-- Botones -->
            <div class="card-actions pt-4 d-flex justify-content-center">
              <template v-if="!card.editing">
                <button
                  type="button"
                  class="button button-primary"
                  @click="startEdit(card)"
                >
                  Editar
                </button>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="button button-primary"
                  @click="saveCard(card)"
                >
                  Guardar
                </button>
                <button
                  type="button"
                  class="button button-light ml-2"
                  @click="cancelEdit(card)"
                >
                  Cancelar
                </button>
              </template>
            </div>
          </b-card-body>
        </b-card>
      </div>

      <div v-if="cards.length === 0" class="col-12">
        <p class="text-muted">No se encontraron reglas de cálculo dinámicas.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { PriceTypeService } from '@/core/services'
import { Alert } from 'wize-admin'

export default {
  data() {
    return {
      cards: [],
    }
  },
  methods: {
    buildCard(pt) {
      return {
        id: pt.id,
        code: pt.code,
        name: pt.name,
        description: pt.description || '',
        formula: pt.formula || '',
        editing: false,
        form: {
          name: pt.name,
          description: pt.description || '',
          formula: pt.formula || '',
        },
        formulaError: null,
        baseInput: '',
        simulateResult: null,
        simulateError: null,
        simulateTimer: null,
      }
    },
    startEdit(card) {
      card.form.name = card.name
      card.form.description = card.description
      card.form.formula = card.formula
      card.formulaError = null
      card.editing = true
    },
    cancelEdit(card) {
      card.form.name = card.name
      card.form.description = card.description
      card.form.formula = card.formula
      card.formulaError = null
      card.editing = false
      this.runSimulate(card)
    },
    async saveCard(card) {
      // Validación frontend
      if (!card.form.name || !card.form.name.trim()) {
        Alert.error('El nombre es obligatorio')
        return
      }
      if (!card.form.formula || !card.form.formula.trim()) {
        card.formulaError = 'La fórmula es obligatoria'
        return
      }
      if (!card.form.formula.includes('base')) {
        card.formulaError = "La fórmula debe usar la variable 'base'"
        return
      }
      card.formulaError = null

      try {
        await this.$store.dispatch('app/loading', true)
        await PriceTypeService.update(card.id, {
          name: card.form.name.trim(),
          formula: card.form.formula.trim(),
          description: card.form.description,
        })
        // Actualizar datos originales
        card.name = card.form.name.trim()
        card.formula = card.form.formula.trim()
        card.description = card.form.description
        card.editing = false
        Alert.success('Regla actualizada correctamente')
        this.runSimulate(card)
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al guardar la regla')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    onFormulaInput(card) {
      card.formulaError = null
      this.onBaseInput(card)
    },
    onBaseInput(card) {
      if (card.simulateTimer) clearTimeout(card.simulateTimer)
      card.simulateTimer = setTimeout(() => this.runSimulate(card), 400)
    },
    async runSimulate(card) {
      const formula = card.editing ? card.form.formula : card.formula
      const amount = parseFloat(card.baseInput)
      if (!formula || isNaN(amount) || amount <= 0) {
        card.simulateResult = null
        card.simulateError = null
        return
      }
      try {
        const res = await PriceTypeService.simulate(formula, amount)
        card.simulateResult = res.payload.result
        card.simulateError = null
      } catch (e) {
        card.simulateResult = null
        card.simulateError = 'Fórmula inválida'
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await PriceTypeService.list()
        const dynamic = (res.payload || []).filter(pt => pt.calculation_type === 'dynamic')
        this.cards = dynamic.map(pt => this.buildCard(pt))
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al cargar las reglas de cálculo')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="stylus" scoped>
.formula-display
  background-color #f4f4f5
  border 1px solid #e4e4e7
  border-radius 6px
  padding 8px 12px
  font-family monospace
  font-size 0.9rem
  color #18181b
  word-break break-all

.simulator-row
  background-color #f9fafb
  border 1px solid #e5e7eb
  border-radius 6px
  padding 12px

.simulator-label
  font-size 0.8rem
  color #6b7280
  min-width 90px
  display inline-block

.simulator-input
  max-width 130px
  display inline-block

.simulator-result
  display flex
  align-items center

.simulator-value
  font-size 1.1rem
  font-weight 600
  color #16a34a

.simulator-error
  font-size 0.85rem
  color #dc2626

.form-error
  font-size 0.8rem
  color #dc2626
  display block
  margin-top 4px

.card-actions
  padding 0 24px 20px

.description-title
  min-height 50px
</style>
